/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { gnoment } from 'shared-ui/gnoment';
import { bindActionCreators } from 'redux';
import compact from 'lodash/compact';
import cloneDeep from 'lodash/cloneDeep';
import countBy from 'lodash/countBy';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import uniqueId from 'lodash/uniqueId';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import { connect } from 'react-redux';
import { from, Subject } from 'rxjs';
import { concatMap, map, retry } from 'rxjs/operators';

import {
    Button,
    ButtonGroup,
    FlexCol,
    FlexRow,
    ModalActions,
    NotFoundPage,
    NotificationActions,
    NotificationContainer,
    ProgressIndicator,
    StyledModal,
} from 'sarsaparilla';
import { withCompatRouter, COMPAT_ROUTER_PROP_TYPES } from 'sarsaparilla/legacy';
import { authModalActions } from 'shared-ui/auth/redux';
import { gaReportingCartReservationsForStepper } from 'shared-ui/utils/gaCommon';
import { FacilityAlertBannerContainer } from 'shared-ui/containers/FacilityAlertBannerContainer';
import {
    withRecaptchaFallback,
    RecaptchaCheckbox,
    isRecaptchaChallengeMessage,
} from 'shared-ui/recaptcha';

import { INVENTORY_PARKPASS } from 'ui-cart/src/constants';

import { MAX_PASSES_IN_CART } from '../constants';
import { RECAPTCHA_ERROR, RECAPTCHA_ERROR2 } from '../constants/recaptcha';
import { memoizeDebounce } from '../utils';
import { getParentAsset, getPrice } from '../api';
import FAQModal from '../components/FAQModal';
import LEGACY_PassSelection from '../components/LEGACY_PassSelection';
import { isDailyPassType } from '../utils/date_time_utils';

import {
    clearValidationErrors,
    createReservations,
    fetchCart,
    fetchFacilityByIdOrSlug,
    fetchPassTypes,
    fetchReservations,
    removeReservation,
    validateReservation,
} from '../actions';
import {
    facilityByIdOrSlugSelectors,
    getErrors,
    isLoaded,
    isLoading,
    passTypesSelectors,
    reservationsSelectors,
    validationSelectors,
} from '../reducers';

import {
    FacilityProps,
    PassTypeProps,
    ReservationDefaults,
    ReservationProps,
} from '../props';

const LoadingTypes = PropTypes.shape({
    facility: PropTypes.bool,
    passTypes: PropTypes.bool,
    reservations: PropTypes.bool,
    facilityByIdOrSlug: PropTypes.bool,
});

export class LEGACY_PassSelectionPage extends Component {
    static defaultProps = {
        fetchCart: () => {},
        fetchFacilityByIdOrSlug: () => {},
        loaded: {},
        loading: {},
        reservations: [],
    };

    static propTypes = {
        router: COMPAT_ROUTER_PROP_TYPES,
        account: PropTypes.shape({
            first_name: PropTypes.string,
            last_name: PropTypes.string,
            email: PropTypes.string,
        }),
        cartReservations: PropTypes.arrayOf(PropTypes.object),
        cartReservationIds: PropTypes.arrayOf(PropTypes.string),
        createReservations: PropTypes.func,
        validateReservation: PropTypes.func,
        facility: FacilityProps,
        fetchCart: PropTypes.func,
        fetchFacilityByIdOrSlug: PropTypes.func,
        fetchPassTypes: PropTypes.func,
        fetchReservations: PropTypes.func,
        loaded: LoadingTypes,
        loading: LoadingTypes,
        passTypes: PropTypes.arrayOf(PassTypeProps),
        removeReservation: PropTypes.func,
        clearValidationErrors: PropTypes.func,
        reservations: PropTypes.arrayOf(ReservationProps),
        // routeParams: { resId: PropTypes.string, siteId: PropTypes.string },
        openLoginModal: PropTypes.func,
        openLoginWithGuestModal: PropTypes.func,
        auth: PropTypes.object,
        csrAccount: PropTypes.any,
        recaptcha: PropTypes.object,
        setNotification: PropTypes.func,
    };

    constructor(props) {
        super(props);

        this.priceSubject = new Subject();
        this.removedReservationIds = [];

        this.state = {
            hasSaveReservationIntent: false,
            parentAsset: [],
            isCsrAccount: false,
            isChallengeModalOpen: false,
            isSubmitting: false,
            needToKnowChecked: false,
            passCounts: {},
            submitAttempted: false,
            reservationValidStatuses: {},
            reservations: props.reservations.filter(
                ({ facility_id: id }) => id === props.router.params.siteId
            ),
        };
    }

    componentDidMount() {
        const { loading, loaded } = this.props;
        const { siteId, resId } = this.props.router.params;
        const searchParams = this.props.router.searchParams;

        this.loadPageData(loading, loaded, {
            ...Object.fromEntries(searchParams.entries()),
            fid: siteId || searchParams.get('fid'),
            rid: resId || searchParams.get('rid'),
        });

        this.priceSubject
            .asObservable()
            .pipe(
                concatMap((reservation) => from(this.getReservationPrice(reservation))),
                retry(5),
                map(({ data }) => data)
            )
            .subscribe((pricing) => {
                const reservations = cloneDeep(this.state.reservations);
                const { id, fees, price, total } = pricing;
                const index = reservations.findIndex(({ id: rId }) => rId === id);
                if (index > -1) {
                    Object.assign(reservations[index], {
                        has_price: true,
                        fees,
                        price,
                        total,
                    });
                    if (!isEqual(this.state.reservations, reservations)) {
                        this.setState({ reservations });
                    }
                }
            });
    }

    componentDidUpdate(prevProps) {
        const { account } = this.props;
        const { hasSaveReservationIntent, isCsrAccount } = this.state;
        const newState = {};

        if (isCsrAccount && account !== prevProps.account) {
            this.setState({ isCsrAccount: false });
        }
        if (!prevProps.account && account && hasSaveReservationIntent) {
            // setState is OK, checking for difference in account and hasSaveReservationIntent
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({ hasSaveReservationIntent: false }, this.saveReservation);
        }
        if (
            this.props.reservations.length &&
            !isEqual(this.props.reservations, prevProps.reservations)
        ) {
            newState.reservations = this.props.reservations.filter(
                ({ facility_id: id }) => id === this.props.router.params.siteId
            );
            newState.passCounts = countBy(this.props.reservations, 'pass_type_id');
            newState.needToKnowChecked = true;
            this.setState((prevState) => ({ ...prevState, ...newState }));
        }
    }

    onPassCountChange = (passTypeId, count) => {
        const { passTypes } = this.props;
        const goingUp = count > (this.state.passCounts[passTypeId] || 0);

        let reservations = cloneDeep(this.state.reservations);
        const reservationValidStatuses = cloneDeep(this.state.reservationValidStatuses);
        let passInfo = {};

        if (!isNil(this.props.account)) {
            passInfo = {
                pass_info: {
                    ...ReservationDefaults.pass_info,
                    holder_info: {
                        first_name: this.props.account?.first_name,
                        last_name: this.props.account?.last_name,
                        email: this.props.account?.email,
                    },
                },
            };
        }
        if (goingUp) {
            const newReservation = {
                ...ReservationDefaults,
                id: uniqueId(),
                is_pending: true,
                facility_id: this.getSiteId(),
                pass_type_id: passTypeId,
                ...passInfo,
            };
            newReservation.pass_info.start_date = gnoment().startOf('day').format();
            newReservation.pass_info.features['start-date'] = gnoment()
                .startOf('day')
                .format();
            const currentPassType = passTypes?.find((item) => item.id === passTypeId);

            newReservation.pass_info.duration = currentPassType?.duration;

            if (currentPassType && isDailyPassType(currentPassType?.duration)) {
                newReservation.pass_info.start_date = '';
                newReservation.pass_info.features['start-date'] = '';
            }
            reservations.push(newReservation);
        } else {
            const oldReservation = reservations
                .concat()
                .reverse()
                .find(({ pass_type_id: ptId }) => ptId === passTypeId);
            if (oldReservation && !oldReservation.is_pending) {
                this.removedReservationIds.push(oldReservation.id);
            }
            reservationValidStatuses[oldReservation?.id] = true;
            reservations = reservations.filter(({ id }) => id !== oldReservation.id);
        }

        this.setState((prevState) => ({
            ...prevState,
            passCounts: {
                ...prevState.passCounts,
                [passTypeId]: count,
            },
            reservations,
            reservationValidStatuses,
        }));
    };

    onReservationChange = memoizeDebounce(
        (reservation, reservationIsValid) => {
            // eslint-disable-next-line react/no-access-state-in-setstate -- I think this is a linting false positive
            const reservations = cloneDeep(this.state.reservations);
            const reservationValidStatuses = cloneDeep(
                this.state.reservationValidStatuses
            );
            const index = reservations.findIndex(({ id }) => id === reservation.id);

            let oldFeatures = cloneDeep(reservation.pass_info.features);
            const newFeatures = cloneDeep(reservation.pass_info.features);

            if (index > -1) {
                oldFeatures = reservations[index].pass_info.features;
                reservations[index] = reservation;
                if (!isEqual(oldFeatures, newFeatures)) {
                    this.priceSubject.next(reservation);
                }
            }

            this.setState({
                reservations,
                reservationValidStatuses: {
                    ...reservationValidStatuses,
                    [reservation.id]: reservationIsValid,
                },
            });
        },
        200,
        { resolver: (res) => res.id }
    );

    getReservationPrice = ({
        id,
        pass_type_id,
        features,
        pass_info: { start_date: startDate },
    }) => {
        const { passTypes } = this.props;
        const currentPassType = passTypes?.find((item) => item.id === pass_type_id);
        if (isDailyPassType(currentPassType?.duration) && isEmpty(startDate)) {
            return '';
        }
        return getPrice({
            pass_type_id,
            features: {
                ...features,
                'start-date': startDate,
            },
        }).then(({ data }) => ({ data: { ...data, id } }));
    };

    getSiteId = () => {
        return (
            this.props.router.params.siteId || this.props.router.searchParams.get('fid')
        );
    };

    getResId = () => {
        return (
            this.props.router.params.resId || this.props.router.searchParams.get('rid')
        );
    };

    getPassTypes = () => {
        const hasPassTypes = !!this.props.passTypes?.length;

        if (!hasPassTypes) {
            return this.props.passTypes;
        }

        const passTypes = this.props.passTypes.filter((item) => {
            const isFieldSales = !!item?.features?.is_field_sales;
            return !isFieldSales;
        });

        return passTypes;
    };

    handleNeedToKnowChecked = (checked) => {
        this.setState({ needToKnowChecked: checked });
    };

    loadPageData = (loading, loaded, queryParams) => {
        if (!loading.facility && !loaded.facility) {
            const facilityId = this.getSiteId();
            this.props.fetchFacilityByIdOrSlug(facilityId).then((facility) => {
                getParentAsset(facility.asset_id).then(({ data }) =>
                    this.setState({ parentAsset: data?.results })
                );
                if (facility.id && !loading.passTypes && !loaded.passTypes) {
                    this.props.fetchPassTypes(facility.id);
                }
            });
        }

        if (loaded.facility) {
            const { facility } = this.props;
            const facilityId = this.getSiteId();

            if (facilityId && !facility) {
                return;
            }
        }

        this.props.fetchCart();

        const { rid } = queryParams;
        if (
            (rid || this.props.cartReservationIds.length) &&
            !loading.reservations &&
            !loaded.reservations
        ) {
            this.props.fetchReservations(
                queryParams.rid ? [queryParams.rid] : this.props.cartReservationIds
            );
        }
    };

    handleCheckErrors = async (reservations) => {
        const reservationsArray = isNil(reservations) ? [] : reservations;
        const errorsCheck = await Promise.all(
            reservationsArray.map(async (reservation, index) => {
                const error = await this.props.validateReservation(index, reservation);
                return error;
            })
        );
        const errorsFiltered = errorsCheck.filter((item) => !isNil(item));
        return errorsFiltered;
    };

    hasErrors = () => {
        const { needToKnowChecked, reservations } = this.state;
        let hasErrors = false;

        reservations.forEach((item) => {
            if (
                item?.holder_info?.email_confirmation &&
                item?.holder_info?.email !== item?.holder_info?.email_confirmation
            ) {
                hasErrors = true;
            }

            if (!item?.holder_info?.zip_code) {
                hasErrors = true;
            }
        });

        if (!needToKnowChecked) {
            hasErrors = true;
        }

        return hasErrors;
    };

    saveReservation = async () => {
        this.setState({
            isSubmitting: true,
        });
        const { account, csrAccount, auth } = this.props;
        const passTypes = this.getPassTypes();
        const { needToKnowChecked } = this.state;
        const { reservations } = cloneDeep(this.state);

        const errorsResp = await this.handleCheckErrors(reservations);
        if (errorsResp.length > 0) {
            this.setState({
                submitAttempted: false,
                isSubmitting: false,
            });
            window.scrollTo(0, 0);
            return;
        }

        const loggedIn = account != null;
        if (!loggedIn && isNil(csrAccount)) {
            this.props.openLoginWithGuestModal();

            this.setState({
                hasSaveReservationIntent: true,
                isSubmitting: false,
            });
            return;
        }
        if (!isNil(csrAccount) && (!loggedIn || auth.isGuest)) {
            window.scrollTo(0, 0);

            this.props.openLoginModal();
            this.setState({
                isCsrAccount: true,
                isSubmitting: false,
            });
            return;
        }

        // Delete pending `id`s before sending to server.
        // eslint-disable-next-line no-param-reassign
        reservations.filter(({ is_pending }) => is_pending).forEach((r) => delete r.id);

        if (needToKnowChecked) {
            reservations.forEach((reservation) => {
                const passType = passTypes.find(
                    ({ id }) => id === reservation.pass_type_id
                );
                if (get(passType, 'features.accepted', []).includes('need_to_know')) {
                    Object.assign(reservation.features, {
                        accepted: { need_to_know: true },
                    });
                }
            });
        }

        try {
            if (this.hasErrors()) {
                this.setState({
                    isSubmitting: false,
                });
                throw new Error();
            }

            const { recaptcha } = this.props;

            if (recaptcha.isChallengeRequired && !this.state.isChallengeModalOpen) {
                this.setState({ isChallengeModalOpen: true, isSubmitting: false });
                return;
            }

            let recaptchaToken = null;
            try {
                recaptchaToken = await recaptcha.getToken('create_sitepass_reservation');
            } catch {
                recaptchaToken = null;
            }

            const recaptchaOptions = {
                recaptcha_token: recaptchaToken,
                recaptcha_version: recaptcha.isChallengeRequired ? 'V2' : 'V3',
            };

            await this.props.createReservations(reservations, recaptchaOptions);

            // Reservation succeeded, so tear down any active challenge fallback.
            if (
                this.state.isChallengeModalOpen ||
                this.props.recaptcha.isChallengeRequired
            ) {
                this.props.recaptcha.resetChallenge();
            }
            this.setState({ isChallengeModalOpen: false });

            // Remove any IDs the user removed via the pass counters.
            this.removedReservationIds.reduce(async (promise, id) => {
                await promise;
                return this.props.removeReservation(id);
            }, Promise.resolve());

            setTimeout(
                () => window.location.assign('/cart'),
                1000 * this.removedReservationIds.length
            );
        } catch (e) {
            const { shouldRetry, isFailed } =
                this.props.recaptcha.handleRecaptchaError(e);

            // The invisible/score assessment failed and the service is asking
            // for the visible checkbox challenge. handleRecaptchaError has
            // already flipped the hook into v2 mode; open the fallback modal
            // instead of surfacing an error. Solving it re-submits with a
            // V2 token.
            if (shouldRetry) {
                this.setState({ isChallengeModalOpen: true, isSubmitting: false });
                return;
            }

            // reCAPTCHA verification hard-failed. Surface a distinct message
            // depending on which step failed: RECAPTCHA_ERROR2 when the user's
            // checkbox challenge (V2) submission was rejected, otherwise
            // RECAPTCHA_ERROR for the invisible/enterprise assessment failure.
            if (isFailed) {
                const failedCheckbox = this.props.recaptcha.isChallengeRequired;
                this.props.recaptcha.resetChallenge();
                this.setState({
                    submitAttempted: true,
                    isSubmitting: false,
                    isChallengeModalOpen: false,
                });
                this.props.setNotification(
                    'error',
                    failedCheckbox ? RECAPTCHA_ERROR2 : RECAPTCHA_ERROR
                );
                window.scrollTo(0, 0);
                return;
            }

            this.setState({ submitAttempted: true, isSubmitting: false });
            this.handleCheckErrors();
            window.scrollTo(0, 0);
        }
    };

    removeReservation = async (id) => {
        let reservations = cloneDeep(this.state.reservations);
        const reservationValidStatuses = cloneDeep(this.state.reservationValidStatuses);

        const index = reservations.findIndex(({ id: rid }) => id === rid);
        if (index > -1) {
            // Remove any validation errors
            this.props.clearValidationErrors(index);
            // Remove on the server
            try {
                await this.props.removeReservation(id);
                const res = reservations[index];
                const passTypeId = res.pass_type_id;
                reservationValidStatuses[res.id] = true;
                delete reservations[index];
                reservations = compact(reservations);

                this.setState(
                    (prevState) => ({
                        passCounts: {
                            ...prevState.passCounts,
                            [passTypeId]: prevState.passCounts[passTypeId] - 1,
                        },
                        reservations,
                        reservationValidStatuses,
                    }),
                    () => {
                        const reservationId = this.getResId();
                        if (reservationId) {
                            window.location.assign('/cart');
                        }
                    }
                );
            } catch (e) {
                // No-op. Errors will propagate through props.errors
                window.scrollTo(0, 0);
            }
        }
    };

    renderProceedButton = () => {
        const { reservations, isSubmitting } = this.state;
        const reservationId = this.getResId();
        const hasReservation = reservationId || this.props.reservations.length;
        const hasPendingReservation = reservations.length;
        const buttonText = hasReservation ? 'Save Changes' : 'Add to Cart';

        return (
            <Button
                shouldFitContainer
                size="lg"
                className="parkpass-proceed-button"
                isDisabled={isSubmitting || !(hasReservation || hasPendingReservation)}
                onClick={this.saveReservation}
            >
                {buttonText}
            </Button>
        );
    };

    renderPassSelection = () => {
        const {
            account,
            cartReservationIds,
            facility,
            loading,
            loaded,
            passTypes: passTypesRaw,
        } = this.props;
        const {
            needToKnowChecked,
            passCounts,
            reservations,
            isCsrAccount,
            parentAsset,
            isSubmitting,
        } = this.state;
        const facilityId = this.getSiteId();
        const reservationId = this.getResId();
        const facilities = [facility];
        const faqs = get(facility, 'contents.faq', []);
        const maxTotalGuestsReached =
            cartReservationIds.length + reservations.length >= MAX_PASSES_IN_CART;
        const passTypes = this.getPassTypes();

        return (
            <LEGACY_PassSelection
                facilities={facilities}
                facility={facility}
                facilityId={facilityId}
                handleNeedToKnowChecked={this.handleNeedToKnowChecked}
                parentAsset={parentAsset}
                loaded={loaded}
                loading={loading}
                loggedIn={account != null}
                maxTotalGuestsReached={maxTotalGuestsReached}
                needToKnowChecked={needToKnowChecked}
                isCsrAccount={isCsrAccount}
                passCounts={passCounts}
                passTypeId={this.props.router.params.siteId}
                passTypes={passTypes}
                passTypesRaw={passTypesRaw}
                proceedButton={this.renderProceedButton()}
                isSubmitting={isSubmitting}
                reservationId={reservationId}
                reservations={reservations}
                submitAttempted={this.state.submitAttempted}
                onPassCountChange={this.onPassCountChange}
                onRemoveReservation={this.removeReservation}
                onReservationChange={this.onReservationChange}
            >
                <div>
                    <FlexRow hasGutters={false} className="parkpass-proceed-button-wrap">
                        <FlexCol>
                            <ButtonGroup>
                                {this.renderProceedButton()}
                                {reservationId && (
                                    <Button
                                        className="mt-1"
                                        shouldFitContainer
                                        size="lg"
                                        appearance="tertiary"
                                        onClick={() => {
                                            window.location = '/cart';
                                            return null;
                                        }}
                                    >
                                        {reservationId ? 'Cancel Changes' : 'Cancel'}
                                    </Button>
                                )}
                            </ButtonGroup>
                        </FlexCol>
                    </FlexRow>
                    <FlexRow>
                        <FlexCol>
                            <FAQModal faqs={faqs} />
                        </FlexCol>
                    </FlexRow>
                </div>
            </LEGACY_PassSelection>
        );
    };

    render() {
        const { cartReservations, facility, loading, loaded } = this.props;

        const steps = ['Pass Selection', 'Shopping Cart', 'Payment', 'Confirmation'];

        if (loaded.facilityByIdOrSlug && !loading.facilityByIdOrSlug && !facility?.id) {
            return <NotFoundPage />;
        }
        if (facility.is_external_search_disabled && isEmpty(this.getResId())) {
            return <NotFoundPage />;
        }

        return (
            <div className="rec-parkpass-page">
                <NotificationContainer className="mb-0" />
                <div className="parkpass-order-details-heading">
                    <ProgressIndicator
                        eventToReportToGA={gaReportingCartReservationsForStepper(
                            cartReservations,
                            steps,
                            0
                        )}
                        shouldReportToGA
                        steps={steps}
                        activeStep={1}
                    />
                </div>
                <FacilityAlertBannerContainer />

                <div className="rec-parkpass-section-outer-wrap">
                    <div className="rec-parkpass-wrap">{this.renderPassSelection()}</div>

                    <div className="rec-parkpass-bottom" />
                </div>
                {this.state.isChallengeModalOpen && (
                    <StyledModal
                        isOpen={this.state.isChallengeModalOpen}
                        heading="We need to verify that you're a human"
                        shouldCloseOnOverlayClick={false}
                        shouldShowCloseButton={false}
                    >
                        <RecaptchaCheckbox
                            {...this.props.recaptcha.recaptchaCheckboxProps}
                            isFallbackOnly
                            action="create_sitepass_reservation"
                        />
                        <ModalActions>
                            <Button
                                onClick={() => this.saveReservation()}
                                isDisabled={this.props.recaptcha.isV2Invalid}
                                gaTrackingId="sitepass-legacy-recaptcha-submit"
                                appearance="tertiary"
                            >
                                Continue Booking
                            </Button>
                        </ModalActions>
                    </StyledModal>
                )}
            </div>
        );
    }
}

const mapStateToProps = (state) => {
    const errors = getErrors(state, 'reservations', 'facilityByIdOrSlug', 'passTypes');

    Object.keys(errors).forEach((key) => {
        if (isRecaptchaChallengeMessage(errors[key])) {
            delete errors[key];
        }
    });

    const validationErrors = validationSelectors.getValidationErrors(state);

    if (validationErrors.length) {
        errors.validation = validationErrors;
    }

    const reservations = reservationsSelectors.getReservations(state);

    return {
        account: state.auth && state.auth.account && state.auth.account.account,
        csrAccount: get(state, 'auth.csrAccount', {}),
        auth: get(state, 'auth', {}),
        cartReservations: state.cart.reservations,
        cartReservationIds: state.cart.reservations
            .filter(({ inventory_type: type }) => type === INVENTORY_PARKPASS)
            .map(({ reservation_id: id }) => id),
        facility: facilityByIdOrSlugSelectors.getFacilityByIdOrSlug(state),
        loaded: isLoaded(state, 'reservations', 'facilityByIdOrSlug', 'passTypes'),
        loading: isLoading(state, 'reservations', 'facilityByIdOrSlug', 'passTypes'),
        passTypes: passTypesSelectors.getPassTypes(state),
        reservations: Array.isArray(reservations) ? reservations : [reservations],
    };
};

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            createReservations,
            fetchCart,
            fetchFacilityByIdOrSlug,
            fetchPassTypes,
            fetchReservations,
            removeReservation,
            validateReservation,
            clearValidationErrors,
            openLoginModal: authModalActions.openLoginModal,
            openLoginWithGuestModal: authModalActions.openLoginWithGuestModal,
            setNotification: NotificationActions.setNotification,
        },
        dispatch
    );

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withRecaptchaFallback(withCompatRouter(LEGACY_PassSelectionPage)));

// cspell:ignore parkpass, ptid, setstate
